import { ChartFormField } from 'interfaces/components/modalTextField';
import {
  yoyArrGrowthField,
  newLogoVelocityField,
  cacPaybackField,
  grossMarginField,
  fcfMarginField,
  newLogoArrOfGrossNewField,
  expansionArrOfGrossNewField,
  newLogoArrField,
  expansionArrField,
  quickRatioField,
  grossChurnArrField,
  netDollarRetentionField,
  grossDollarRetentionField,
  BOPArrField,
  netMagicNumberField,
  smOpExField,
  ruleOf40Field,
  burnMultipleField,
  fcfField,
  netNewArrField,
  smPercentOpexField,
  rdPercentOpexField,
  gaPercentOpexField,
  gaHeadcountPercentField,
  smHeadcountPercentField,
  rdHeadcountPercentField,
  totalHeadcountField,
  rdFteField,
  smFteField,
  gaFteField,
  arrPerFteField,
  nestedCurrentArrField,
  opexPerFteField,
  totalOpexField,
  rdAbsoluteOpexField,
  smAbsoluteOpexField,
  gaAbsoluteOpexField,
  annualRevenueChartField
} from './fields';
import { CalculateFunction } from 'interfaces/constants/CalculateModalConfig';
import {
  calculateArrPerFte,
  calculateBurnMultiple,
  calculateExpansionArrOfGrossNew,
  calculateGAHeadPercent,
  calculateGAOpex,
  calculateGrossDollarRetention,
  calculateNetDollarRetention,
  calculateNetMagicNumber,
  calculateNewLogoArrOfGrossNew,
  calculateOpexPerFte,
  calculateQuickRatio,
  calculateRDHeadPercent,
  calculateRDOpex,
  calculateSMHeadPercent,
  calculateSMOpex
} from 'utils/helpers/calculateFunctions';

interface CalculatedFields {
  field: ChartFormField;
  calculate?: CalculateFunction;
}

export interface ChartFieldBase {
  calculatedFields: CalculatedFields[];
  nestedFields?: ChartFormField[];
  title?: string;
}

type ChartFieldConfig = Record<string, ChartFieldBase>;

export const chartsFormConfig: ChartFieldConfig = {
  'YoY ARR Growth': {
    calculatedFields: [
      {
        field: yoyArrGrowthField
      }
    ]
  },
  'New Logo Velocity': {
    calculatedFields: [
      {
        field: newLogoVelocityField
      }
    ]
  },
  'CAC Payback': {
    calculatedFields: [
      {
        field: cacPaybackField
      }
    ]
  },
  'Gross Margin': {
    calculatedFields: [
      {
        field: grossMarginField
      }
    ]
  },
  'FCF Margin': {
    calculatedFields: [
      {
        field: fcfMarginField
      }
    ]
  },
  'OpEx as a % of Revenue': {
    calculatedFields: [
      {
        field: gaPercentOpexField,
        calculate: calculateGAOpex
      },
      {
        field: smPercentOpexField,
        calculate: calculateSMOpex
      },
      {
        field: rdPercentOpexField,
        calculate: calculateRDOpex
      }
    ],
    nestedFields: [
      annualRevenueChartField,
      gaAbsoluteOpexField,
      smAbsoluteOpexField,
      rdAbsoluteOpexField
    ],
    title: 'OpEx as a % of Revenue'
  },
  'Gross New ARR Composition': {
    calculatedFields: [
      {
        field: newLogoArrOfGrossNewField,
        calculate: calculateNewLogoArrOfGrossNew
      },
      {
        field: expansionArrOfGrossNewField,
        calculate: calculateExpansionArrOfGrossNew
      }
    ],
    nestedFields: [newLogoArrField, expansionArrField]
  },
  'Quick Ratio': {
    calculatedFields: [
      {
        field: quickRatioField,
        calculate: calculateQuickRatio
      }
    ],
    nestedFields: [newLogoArrField, expansionArrField, grossChurnArrField]
  },
  'Gross Dollar Retention': {
    calculatedFields: [
      {
        field: grossDollarRetentionField,
        calculate: calculateGrossDollarRetention
      }
    ],
    nestedFields: [
      BOPArrField,
      newLogoArrField,
      expansionArrField,
      grossChurnArrField
    ]
  },
  'Net Dollar Retention': {
    calculatedFields: [
      {
        field: netDollarRetentionField,
        calculate: calculateNetDollarRetention
      }
    ],
    nestedFields: [
      BOPArrField,
      newLogoArrField,
      expansionArrField,
      grossChurnArrField
    ]
  },
  'Net Magic Number': {
    calculatedFields: [
      {
        field: netMagicNumberField,
        calculate: calculateNetMagicNumber
      }
    ],
    nestedFields: [smOpExField, netNewArrField]
  },
  'Rule of 40': {
    calculatedFields: [
      {
        field: yoyArrGrowthField
      },
      {
        field: fcfMarginField
      },
      {
        field: ruleOf40Field
      }
    ]
  },
  'Burn Multiple': {
    calculatedFields: [
      {
        field: burnMultipleField,
        calculate: calculateBurnMultiple
      }
    ],
    nestedFields: [fcfField, netNewArrField]
  },
  'Headcount Distribution': {
    calculatedFields: [
      {
        field: gaHeadcountPercentField,
        calculate: calculateGAHeadPercent
      },
      {
        field: smHeadcountPercentField,
        calculate: calculateSMHeadPercent
      },
      {
        field: rdHeadcountPercentField,
        calculate: calculateRDHeadPercent
      }
    ],
    nestedFields: [totalHeadcountField, gaFteField, smFteField, rdFteField],
    title: 'Headcount Distribution (Total FTEs)'
  },
  'ARR per FTE': {
    calculatedFields: [
      {
        field: arrPerFteField,
        calculate: calculateArrPerFte
      }
    ],
    nestedFields: [nestedCurrentArrField, totalHeadcountField]
  },
  'OpEx per FTE': {
    calculatedFields: [
      {
        field: opexPerFteField,
        calculate: calculateOpexPerFte
      }
    ],
    nestedFields: [totalOpexField, totalHeadcountField]
  }
};
